import { useAtomValue } from 'jotai';
import { isEqual } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import {
  getDefaultKpiSourceHierarchy,
  isLockedKpiSource,
  KpiSourceCatalogEntry,
  KpiSourceHierarchyEntry,
  KpiSourceHierarchyPreferences,
  KpiSourceId,
  KpiSourceResolutionStrategy,
  normalizeKpiSourceHierarchy,
} from '../../../data-models/kpi-source-hierarchy.data-model';
import {
  getOrgPreferenceKey,
  PreferenceKey,
  PreferenceScope,
} from '../../../data-models/preferences-v3.data-model';
import { updateKpiSourceHierarchy } from '../../../services/queries/KpiSourceHierarchyQueries';
import { getErrorMessage } from '../../../services/queryHelpers';
import { kpiResolutionStrategiesAtom, kpiSourceCatalogAtom } from '../../../services/state/AppConfigStateJ';
import { PreferencesServiceV3 } from '../../../util/preferences-service-v3';
import { ConfigStatus } from '../DataDisplay/useDisplayConfigState';

// Org default today; a per-company override would live at `.$<companyId>`.
export const KPI_SOURCE_HIERARCHY_PREF_KEY = getOrgPreferenceKey(PreferenceKey.kpiSourceHierarchy, null);

export const KPI_RECOMPUTE_TOAST = {
  title: 'KPI source settings saved',
  message: 'KPI values are being recomputed with the new settings. This may take a few seconds to complete.',
};

export function readStoredKpiSourceHierarchy(
  catalog: KpiSourceCatalogEntry[]
): KpiSourceHierarchyPreferences {
  return normalizeKpiSourceHierarchy(
    PreferencesServiceV3.get().getPreference<KpiSourceHierarchyPreferences>(
      KPI_SOURCE_HIERARCHY_PREF_KEY,
      PreferenceScope.ORGANIZATION
    ),
    catalog
  );
}

export function useKpiSourcesConfigState() {
  const catalog = useAtomValue(kpiSourceCatalogAtom);
  const strategies = useAtomValue(kpiResolutionStrategiesAtom);
  const { pushInfoToast, pushErrorToast } = useToastMessageState();
  const [hierarchy, setHierarchy] = useState<KpiSourceHierarchyPreferences>(() =>
    readStoredKpiSourceHierarchy(catalog)
  );
  const [editStatus, setEditStatus] = useState<ConfigStatus>('initial');
  const isDefault = useMemo(
    () => isEqual(hierarchy, getDefaultKpiSourceHierarchy(catalog)),
    [catalog, hierarchy]
  );

  // Both go through normalize so the locked entries stay first and enabled
  // whatever the list hands back.
  const reorder = useCallback(
    (sources: KpiSourceHierarchyEntry[]) => {
      setHierarchy((prev) => {
        const kept = new Set(sources.map((entry) => entry.id));
        const untouched = prev.sources.filter((entry) => !kept.has(entry.id));
        return normalizeKpiSourceHierarchy({ ...prev, sources: [...untouched, ...sources] }, catalog);
      });
      setEditStatus('modified');
    },
    [catalog]
  );

  const setStrategy = useCallback(
    (strategy: KpiSourceResolutionStrategy) => {
      if (!strategies.some((candidate) => candidate.id === strategy)) {
        return;
      }
      setHierarchy((prev) => ({ ...prev, strategy }));
      setEditStatus('modified');
    },
    [strategies]
  );

  const toggleSource = useCallback(
    (id: KpiSourceId, enabled: boolean) => {
      const entry = catalog.find((candidate) => candidate.id === id);
      if (!entry || isLockedKpiSource(entry)) {
        return;
      }
      setHierarchy((prev) =>
        normalizeKpiSourceHierarchy(
          {
            ...prev,
            sources: prev.sources.map((entry) => (entry.id === id ? { ...entry, enabled } : entry)),
          },
          catalog
        )
      );
      setEditStatus('modified');
    },
    [catalog]
  );

  const persist = useCallback(
    async (next: KpiSourceHierarchyPreferences) => {
      try {
        const saved = await updateKpiSourceHierarchy(next);
        const value = normalizeKpiSourceHierarchy(saved.value, catalog);
        PreferencesServiceV3.get().cachePreference(
          KPI_SOURCE_HIERARCHY_PREF_KEY,
          value,
          PreferenceScope.ORGANIZATION
        );
        setHierarchy(value);
        setEditStatus('saved');
        pushInfoToast(KPI_RECOMPUTE_TOAST);
      } catch (error) {
        console.error(error);
        pushErrorToast({ message: getErrorMessage(error, 'Failed to save the KPI source hierarchy') });
      }
    },
    [catalog, pushErrorToast, pushInfoToast]
  );

  const onSave = useCallback(() => persist(hierarchy), [hierarchy, persist]);
  const onReset = useCallback(() => persist(getDefaultKpiSourceHierarchy(catalog)), [catalog, persist]);

  return {
    catalog,
    strategies,
    hierarchy,
    editStatus,
    isDefault,
    setStrategy,
    reorder,
    toggleSource,
    onSave,
    onReset,
  };
}
